import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

// Build-target marker — survives minification because it's read at module
// init. scripts/verify-build-target.mjs greps the bundle for this literal to
// refuse cross-environment deploys (incident 2026-05-12).
declare const __BUILD_TARGET_MARKER__: string
;(globalThis as { __uPadelMatchBuildTarget__?: string }).__uPadelMatchBuildTarget__ = __BUILD_TARGET_MARKER__

// The commit this bundle was built from. Assigned the same way and for the same
// reason — a vite `define` only substitutes where the identifier appears, so an
// unreferenced constant never reaches the bundle. The prod deploy reads this off
// DEV hosting to prove the code ran on dev first (verify-dev-ran-this.mjs).
declare const __BUILD_COMMIT__: string
;(globalThis as { __uPadelMatchBuildCommit__?: string }).__uPadelMatchBuildCommit__ = __BUILD_COMMIT__

import { QueryCache, QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query'
import { MotionConfig } from 'motion/react'
import { registerSW } from 'virtual:pwa-register'
import { registerSwUpdater } from '@/core/version/force-update'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import App from './App'
import './index.css'
// i18n scaffold — initialises i18next (es, sync resources) before render so
// useTranslation() works on first paint. Localization foundation; see
// core/i18n/i18n.ts.
import './core/i18n/i18n'
import { initTheme } from './shared/theme'
import { initThemeLab } from './shared/theme-lab/apply'
import { initSentry } from './core/sentry'
import { initAnalytics } from './core/analytics'
import { logger } from '@/core/logger'
import { RootErrorBoundary } from './shared/components/root-error-boundary'
import { PermissionDeniedError } from '@/features/staff/hooks/use-permission-gate'
import { isExpectedError } from '@/core/expected-error'

// Defense-in-depth for invite emails: if any URL carries `invite_token`
// outside `/accept-invite`, hop there before the router runs. The SPA's
// ProtectedRoute strips the query string when redirecting to /login, which
// silently drops invitations whenever someone reaches the root or a
// protected page first. Old emails in the wild (or shared links) are
// re-anchored here without a visible flash.
if (typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search)
  const hasInviteToken = params.get('invite_token')?.trim()
  const onAcceptPath = window.location.pathname === '/accept-invite'
  if (hasInviteToken && !onAcceptPath) {
    window.history.replaceState(
      null,
      '',
      `/accept-invite${window.location.search}`,
    )
  }
}

initTheme()
// Replay any Theme Lab overrides onto :root before React mounts. Keeps
// admin-tuned palettes stable across reloads without a flash of default theme.
initThemeLab()
initSentry()

// E2E seed helpers wiring moved to after queryClient creation so the seed
// surface can take it as an arg. See block below the `const queryClient = ...`.
// Fire-and-forget — Analytics init is async (awaits `isSupported()`). Events
// fired before it resolves are dropped; first `page_view` comes from the
// router-level effect after React mounts, by which time init has completed.
void initAnalytics()

// Register the PWA service worker.
//
// ⚠️ `vite.config.ts` sets `registerType: 'prompt'`, NOT autoUpdate — on
// purpose, because the FCM SW shares the `/` scope. This comment used to claim
// autoUpdate and "the SW itself handles skipWaiting + client claim", which is
// the opposite of what ships. Under `prompt` a waiting worker activates ONLY
// when something calls the function `registerSW` returns — and that return
// value was being thrown away, so nothing ever could. `registerSwUpdater`
// keeps it, which is what makes "Actualizar" able to finish.
//
// The authoritative "update now" path for the app is the Firestore-driven
// check in `core/version/use-version-check.ts` + the silent navigation-
// reload hook in `core/version/use-version-auto-reload.ts`. It catches
// stale builds even on fresh tabs where the SW has nothing new to report.
// `onNeedRefresh` is still wired up as a soft secondary signal in case
// we switch `registerType` to `prompt` later.
if (import.meta.env.PROD) {
  registerSwUpdater(
    registerSW({
      immediate: true,
      onRegisteredSW(swUrl) {
        logger.i(`PWA service worker registered: ${swUrl}`)
      },
      onRegisterError(err) {
        logger.e('PWA service worker registration failed', err)
      },
      onNeedRefresh() {
        window.dispatchEvent(new CustomEvent('pwa:update-available'))
      },
      onOfflineReady() {
        window.dispatchEvent(new CustomEvent('pwa:offline-ready'))
      },
    }),
  )
}

// Route every TanStack Query / Mutation failure to Sentry so silent Firestore
// read failures, broken query keys, and mutation rejections show up alongside
// render errors. Retry budgets still apply — we only hear about the final
// failure, not each intermediate retry.
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Permission-denied from Firestore rules is EXPECTED for visitors
      // whose role lacks the read (e.g. receptionist on /ingresos, owner
      // before staff sync, preview-session on any non-pinned path).
      // The catch already returns null/empty — capturing as Sentry
      // error generates noise on every shift change. Same demotion
      // pattern as CLUBMANAGER-PWA-3 (user-clubs-repository, 2026-05-22).
      // Genuine failures (network, parse, conflict) still capture.
      //
      // Check via the `code` field directly rather than
      // `instanceof FirebaseError`. Tree-shaking + dual bundling of
      // `firebase/app` can leave the SDK throwing a FirebaseError whose
      // constructor identity differs from the one we imported — the
      // `instanceof` check then fails and a benign permission-denied
      // slips through to `logger.e`. Sentry CLUBMANAGER-PWA-3M (2026-05-22)
      // surfaced this on the payments-ledger query in dev: the error
      // payload literally said `code: "permission-denied"` but
      // `instanceof FirebaseError` was false.
      const code = (error as { code?: unknown } | null)?.code
      const denied = code === 'permission-denied'
      if (denied) {
        logger.d('Query permission-denied (expected by rules)', {
          key: query.queryKey,
        })
        return
      }
      logger.e('Query failed', { key: query.queryKey, error })
      Sentry.captureException(error, {
        tags: { source: 'tanstack-query' },
        contexts: { query: { key: JSON.stringify(query.queryKey) } },
      })
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      // Permission rejections are expected for custom-role users (and a sign
      // the UI disabled-state didn't catch them first). Surface the role's
      // error message as a toast so the action doesn't silently no-op.
      if (error instanceof PermissionDeniedError) {
        toast.error(error.message)
        return
      }
      // A guard that refuses on purpose is an answer, not a defect — the
      // surface has already turned it into a confirm or an explanation. Logging
      // it would send a Sentry issue every time a club is told "esas pistas ya
      // tienen reservas", which is the feature working.
      if (isExpectedError(error)) return
      // Use raw console.error (not logger.e) — logger.e fires its OWN
      // Sentry capture (a captureMessage of the JSON-stringified payload),
      // which created duplicate noisy issues alongside the proper
      // captureException below (one grouped by message text, one by error
      // class). The captureException path is the right one because it
      // groups by exception type and lets ignoreErrors filter validation
      // failures by class name. (Sentry CLUBMANAGER-PWA-1K + 1P + 1R
      // were the wrapper-message issues this fix removes.)
      console.error('[app] Mutation failed', { key: mutation.options.mutationKey, error })
      Sentry.captureException(error, {
        tags: { source: 'tanstack-mutation' },
        contexts: {
          mutation: { key: JSON.stringify(mutation.options.mutationKey ?? null) },
        },
      })
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// E2E seed helpers — only mounted when `VITE_E2E_HELPERS=true` (dev only,
// per `.env.development`). Adds a `window.__e2eSeed` surface that
// Playwright specs reach via `page.evaluate(...)` to seed deterministic
// bookings + payments. Passes the queryClient in so the seed surface can
// expose `__e2eSeed.invalidate()` — without it, seeded docs land in
// Firestore but the dashboard/Hoy/Bookings queries serve stale TanStack
// Query cache, and assertions time out. Documented FIXME at
// test/e2e/regression/cobrar-amount-consistency.spec.ts.
if (import.meta.env.VITE_E2E_HELPERS === 'true') {
  void import('./e2e-seed').then((m) => m.installE2eHelpers(queryClient))
}

// Last-resort capture for anything that slips past the React boundary and the
// default browser integrations — e.g. an error thrown inside a setTimeout /
// event listener that React never sees.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (ev) => {
    Sentry.captureException(ev.reason, { tags: { source: 'unhandledrejection' } })
  })
  window.addEventListener('error', (ev) => {
    Sentry.captureException(ev.error ?? ev.message, { tags: { source: 'window.onerror' } })
  })
  // When the device comes back online, Firestore's offline write queue flushes
  // automatically and active onSnapshot subscriptions catch up — but any
  // TanStack Query cached without a live listener (CRM lists, dashboard stats,
  // settings) keeps serving the offline snapshot. Invalidate everything so the
  // first paint after reconnect reflects whatever changed elsewhere during the
  // outage. Cheap: queries that aren't currently rendered just bump their
  // staleness flag and refetch on next mount.
  window.addEventListener('online', () => {
    void queryClient.invalidateQueries()
  })
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element #root not found')

// React 19 exposes hooks for caught + uncaught errors at the root level. We
// forward both to Sentry so a render error that escapes the ErrorBoundary
// (or one recovered by a nested boundary) still reaches the dashboard.
createRoot(rootElement, {
  onCaughtError: (error) => {
    Sentry.captureException(error, { tags: { source: 'react-caught' } })
  },
  onUncaughtError: (error) => {
    Sentry.captureException(error, { tags: { source: 'react-uncaught' } })
  },
}).render(
  <StrictMode>
    <AppTree />
  </StrictMode>,
)

/**
 * App provider tree. In e2e builds (`VITE_E2E_HELPERS=true`, dev/preview only)
 * it's wrapped in `MotionConfig reducedMotion="always"`, which disables
 * motion's transform + layout animations (opacity/colour still animate) so
 * Playwright sees stable element bounding boxes. Without it the data-list rows'
 * `popLayout` + `layout` animations keep row positions jittering after a seed,
 * tripping Playwright's "element is not stable" actionability check. The flag
 * never ships to prod, so real users keep the full motion design.
 */
function AppTree() {
  const tree = (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </RootErrorBoundary>
  )
  if (import.meta.env.VITE_E2E_HELPERS === 'true') {
    return <MotionConfig reducedMotion="always">{tree}</MotionConfig>
  }
  return tree
}
